import * as React from "react";
import { ProductHeader, ProductImagesCarousel } from "./components";
import { useNavigate, useParams } from "react-router-dom";
import Api from "@services/Api";
import { toastError } from "@utils/functions";
import { Product, ProductVariation, VariationsValuesPayload } from "@utils/types";
import Loading from "@components/Loading";
import Badge from "react-bootstrap/Badge";
import { FiShare2 } from "react-icons/fi";
import { Money } from "@utils/currency";
import VariationsGrid from "@components/VariationsGrid";
import Styled from "./styled";
import { RootState, useAppDispatch } from "@services/Redux";
import { addProductToOrder, removeProductFromOrder } from "@services/Redux/order";
import { useSelector } from "react-redux";
import { MdCancel } from "react-icons/md";
import PulseLoader from "react-spinners/PulseLoader";
import { setSelectedProduct } from "@services/Redux/app";
import ReactGA from "react-ga4";
import { useCookies } from "react-cookie";
import MetaPixel from "@utils/meta/MetaPixel";

const ProductScreen: React.FC = () => {
  const { product_id } = useParams();
  const companyData = useSelector((state: RootState) => state.company.data);
  const order = useSelector((state: RootState) => state.order);
  const { loadingAddProductRequest, loadingOrder, loadingRemoveProductFromOrder } = useSelector((state: RootState) => state.order);
  const [product, setProduct] = React.useState<Product>();
  const [variations, setVariations] = React.useState<ProductVariation[]>([]);
  const [observations, setObservations] = React.useState("");
  const [variationsData, setVariationsData] = React.useState<VariationsValuesPayload[]>([]);
  const [loadingProduct, setLoadingProduct] = React.useState(true);
  const [cookies] = useCookies([`company_${companyData.code}_seller_id`]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  React.useEffect(() => {
    fetchProduct();
    // eslint-disable-next-line
  }, [product_id]);

  React.useEffect(() => {
    if (product) {
      if (order.data?.items?.find((orderItem) => orderItem.product.id === product.id)?.observations) {
        setObservations(order.data?.items.find((orderItem) => orderItem.product.id === product.id)?.observations ?? "");
      }
    }
    // eslint-disable-next-line
  }, [product, order]);

  const fetchProduct = async () => {
    try {
      const { data } = await Api.get(`/product?product_id=${product_id}`);
      const variationsResponse = await Api.get(`/product/${product_id}/variation`);
      if (!data.products[0]) return navigate("/404", { replace: true });
      setProduct(data.products[0]);
      ReactGA.gtag("event", "view_item", {
        items: [
          {
            item_id: data.products[0].id,
            item_name: data.products[0].name,
            price: data.products[0].price,
            item_category: data.products[0].categories?.[0]?.name,
          },
        ],
        currency: "BRL",
      });
      MetaPixel.trackCustom("view_item", {
        items: [
          {
            item_id: data.products[0].id,
            item_name: data.products[0].name,
            price: data.products[0].price,
            item_category: data.products[0].categories?.[0]?.name,
          },
        ],
        currency: "BRL",
      });
      dispatch(setSelectedProduct(data.products[0]));
      setVariations(variationsResponse.data.variations);
      /* Checking for product observations */
      setLoadingProduct(false);
    } catch (error) {
      console.log(error, "fetchProduct() error");
      toastError(error);
    }
  };

  const handleAddProduct = () => {
    if (!product) return;
    if (variationsData.filter((variation) => variation.quantity !== 0).length === 0) {
      toastError("Por favor, selecione pelo menos um produto");
      return;
    }

    let sellerId = localStorage.getItem(`company_${companyData.code}_seller_id`);
    if (!sellerId) {
      sellerId = cookies[`company_${companyData.code}_seller_id`];
    } else {
    }

    const payload = {
      observations,
      seller_id: sellerId,
      product_id: product.id,
      variations: variationsData.map((v) => ({ id: v.variation.id, quantity: v.quantity })),
    };
    dispatch(addProductToOrder(payload));
    for (const variation of variationsData) {
      if (variation.quantity <= 0) continue;
      ReactGA.gtag("event", "add_to_cart", {
        currency: "BRL",
        value: product.price,
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            price: `${Money(variation.variation.price).value}`,
            quantity: variationsData.reduce((acc, cur) => acc + cur.quantity, 0),
            item_category: product.categories?.[0].name,
            item_variant: `${variation.variation?.color?.name}`,
            size: variation.variation?.size?.name,
          },
        ],
      });
      MetaPixel.trackCustom("add_to_cart", {
        currency: "BRL",
        value: product.price,
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            price: `${Money(variation.variation.price).value}`,
            quantity: variationsData.reduce((acc, cur) => acc + cur.quantity, 0),
            item_category: product.categories?.[0].name,
            item_variant: `${variation.variation?.color?.name}`,
            size: variation.variation?.size?.name,
          },
        ],
      });
    }
  };

  const handleClear = () => {
    if (!product) return;
    if (variationsData.filter((variation) => variation.quantity !== 0).length === 0) {
      toastError("A sua grade de produtos já está vazia!");
    }
    dispatch(removeProductFromOrder({ product_id: product.id, variations: [] }));
  };

  const handleShareClick = () => {
    navigator.share({ url: window.location.href });
  };

  if (loadingProduct) return <Loading />;

  return (
    <>
      <ProductHeader />
      {!loadingProduct && product && (
        <Styled.ContentContainer className={"row px-2"}>
          <div className={"col-12 p-0 mb-3"}>
            <ProductImagesCarousel product={product} />
          </div>
          <div className={"col-6 d-flex align-items-center"} onClick={handleShareClick}>
            <FiShare2 size={24} />
          </div>
          <div className={"col-6 d-flex justify-content-end align-items-center"}>
            {!variations.some((variation) => variation.stock > 0) && product.stock_enabled ? (
              <Badge className="py-2  mx-5 justify-content-center" bg="secondary">
                <div className="d-flex justify-content-center">
                  <MdCancel className="mx-2" />
                  Produto temporariamente sem estoque
                </div>
              </Badge>
            ) : product.on_sale ? (
              <Badge className="mx-3 py-2" bg="success">
                Promoção!
              </Badge>
            ) : null}
            <b style={{ fontSize: 18 }}>{product.lowest_price && Money(product.lowest_price).format()}</b>
          </div>
          <div className={"col-12 my-2"}>
            <span>
              Código: <b>{product.ref}</b>
            </span>
          </div>
          <div className={"col-12 mb-2"}>
            <span style={{ fontSize: 18 }}>
              <b>{product.name}</b>
            </span>
          </div>

          <Styled.DescriptionContainer
            className="text-break"
            dangerouslySetInnerHTML={{ __html: product.description }}
            style={{ whiteSpace: "pre-line" }}
          ></Styled.DescriptionContainer>
          <div className={"col-12 mb-3 mt-3"}>
            <VariationsGrid
              product={product}
              variations={variations}
              onValuesChange={(values) => {
                setVariationsData(values);
              }}
            />
          </div>
          {companyData.is_product_observations_enabled && (
            <div className={"col-12 mb-3"}>
              <label>Observações</label>
              <textarea
                value={observations}
                onChange={(e) => {
                  setObservations(e.target.value);
                }}
                className={"form-control"}
                placeholder={"Coloque aqui observações sobre cores etc..."}
              />
            </div>
          )}
          <div className={"col-5"}>
            <Styled.ClearButton disabled={loadingAddProductRequest || loadingOrder || loadingRemoveProductFromOrder} onClick={handleClear}>
              {loadingOrder || loadingRemoveProductFromOrder ? <PulseLoader size={8} color={"black"} /> : "Limpar"}
            </Styled.ClearButton>
          </div>
          <div className={"col-7"}>
            <Styled.AddToCardButton onClick={handleAddProduct} disabled={loadingAddProductRequest || loadingOrder || loadingRemoveProductFromOrder}>
              {loadingAddProductRequest || loadingAddProductRequest ? <PulseLoader color={"white"} size={6} /> : "Adicionar à sacola"}
            </Styled.AddToCardButton>
          </div>
        </Styled.ContentContainer>
      )}
    </>
  );
};

export default ProductScreen;
